<template>
  <a
    class="recommend-item"
    :href="jumpUrl"
    :title="`${bookName}在线阅读`"
    target="_blank"
    :data-eid="dataEid"
    :data-showeid="dataShoweid"
    :data-bid="bookId"
    :data-l1="dataL1"
    :data-rid="dataRid"
  >
    <!-- 左侧封面 -->
    <div class="recommend-item-cover-wrap">
      <img
        v-if="bookId"
        class="recommend-item-cover"
        :src="coverUrl"
        :alt="`${bookName}封面`"
        loading="lazy"
      />
      <span class="recommend-item-cover-logo" aria-hidden="true">
        起点中文网
      </span>
    </div>

    <!-- 右侧信息 -->
    <div class="recommend-item-info">
      <h3 class="recommend-item-title">{{ bookName }}</h3>

      <p v-if="desc" class="recommend-item-desc">{{ desc }}</p>

      <div class="recommend-item-footer">
        <span class="recommend-item-author">{{ authorName || '' }}</span>
        <div class="recommend-item-tags">
          <span v-if="categoryText" class="recommend-item-tag">
            {{ categoryText }}
          </span>
          <span v-if="serialStatus" class="recommend-item-tag">
            {{ serialStatus }}
          </span>
          <span v-if="wordCount" class="recommend-item-tag">
            {{ wordCount }}
          </span>
        </div>
      </div>
    </div>
  </a>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { getBookCover } from '@share/utils/url'

const props = defineProps({
  bookId: {
    type: String,
    default: '',
  },
  bookName: {
    type: String,
    default: '',
  },
  /** 简介 / 最新章节 */
  desc: {
    type: String,
    default: '',
  },
  authorName: {
    type: String,
    default: '',
  },
  categoryText: {
    type: String,
    default: '',
  },
  serialStatus: {
    type: String,
    default: '',
  },
  wordCount: {
    type: String,
    default: '',
  },
  jumpUrl: {
    type: String,
    default: '',
  },
  /** 上报参数 */
  dataEid: {
    type: String,
    default: '',
  },
  dataShoweid: {
    type: String,
    default: '',
  },
  dataL1: {
    type: String,
    default: '',
  },
  dataRid: {
    type: [Number, String],
    default: 0,
  },
})

const coverUrl = computed<string>(() => {
  if (!props.bookId) return ''
  const src = getBookCover(props.bookId, 150)
  // 兼容 //xxx 与 https:xxx 两种返回形式
  if (!src) return ''
  if (src.startsWith('http')) return src
  return `https:${src}`
})
</script>

<style lang="scss" scoped>
/**
 * 推荐单项卡片 —— 参考设计稿 pc_recommend_2.png
 * 左侧封面 + 右侧信息（标题 / 描述 / 作者 / 标签组）
 */
.recommend-item {
  display: flex;
  gap: 20px;
  padding: 16px;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s, transform 0.2s, background-color 0.2s;
  min-width: 0;

  &:hover {
    background: #fff;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);

    .recommend-item-title {
      color: #fc3c48;
    }

    .recommend-item-cover {
      transform: scale(1.03);
    }
  }
}

/* 左侧封面 */
.recommend-item-cover-wrap {
  position: relative;
  flex-shrink: 0;
  width: 110px;
  height: 148px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f6f8;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.recommend-item-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s;
}

/* 封面左上角 “起点中文网” 小 logo —— 与设计稿一致 */
.recommend-item-cover-logo {
  position: absolute;
  top: 0;
  left: 0;
  padding: 2px 6px;
  font-size: 10px;
  line-height: 14px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 0 0 6px 0;
  letter-spacing: 0.2px;
  transform: scale(0.9);
  transform-origin: top left;
  white-space: nowrap;
  pointer-events: none;
}

/* 右侧信息 */
.recommend-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}

.recommend-item-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.15s;
}

.recommend-item-desc {
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 22px;
  color: #666;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

.recommend-item-footer {
  margin-top: auto;
  padding-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.recommend-item-author {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recommend-item-tags {
  flex-shrink: 0;
  display: flex;
  gap: 6px;
}

.recommend-item-tag {
  padding: 2px 8px;
  font-size: 12px;
  line-height: 18px;
  color: #888;
  background: #f5f5f7;
  border-radius: 4px;
  white-space: nowrap;
}
</style>
